import React, { useEffect, useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import CloseIcon from 'src/images/CloseIcon';
import { palette } from 'src/styles/colors';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';
import ButtonPrimary from 'src/elements/buttons/2023_Q4/ButtonPrimary';
import { IStaticContentfulImages } from 'src/types/contentful';
import { createHubSpotForm, prefillField, submitHubSpotFormBuilder } from 'src/functions/hubspot/hubspot.service';
import { usePopupController } from 'src/hooks/usePopupController';
import { useFormResultController } from 'src/hooks/useFormResultController';
import ThankYouWithLink from 'src/molecules/Standard/ProfitSales/HubSpotForm/ThankYouWithLink';
import {
  EmailPopupHubspotForm,
  EmailPopupMainBackgroundImage,
  EmailPopupMainBackgroundOverlay,
  EmailPopupMainClose,
  EmailPopupMainContainer,
  EmailPopupMainForm,
  EmailPopupMainImage,
  EmailPopupMainLayout,
  EmailPopupMainText,
  EmailPopupMainThankYou,
  EmailPopupMainWrapper,
} from './ProfitSalesEmailPopup.styles';

const submitHubSpotForm = submitHubSpotFormBuilder('#hubspotEmailPopupForm');

export interface ProfitSalesEmailPopupMainProps {
  customization?: {
    title?: string;
    description?: string;
    shownStorageId?: string;
  };
}

const ProfitSalesEmailPopupMain = ({ customization }: ProfitSalesEmailPopupMainProps): JSX.Element => {
  const { peopleMagicProfitSales, peopleMagicProfitSalesBackground } = useStaticQuery<IStaticContentfulImages>(graphql`
    {
      peopleMagicProfitSales: contentfulAsset(contentful_id: { eq: "35j7V44tGXiTT9g7U5Me2V" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 800)
      }
      peopleMagicProfitSalesBackground: contentfulAsset(contentful_id: { eq: "1Ggc89CCZuPx4G8lQ7D1wM" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1600)
      }
    }
  `);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [failureVisible, setFailureVisible] = useState(false);
  const [blockButton, setBlockButton] = useState(false);

  const pauseCompensationRef = useRef(0);

  const { isPaused } = usePopupController();
  const { isEmailSubscriptionSubmitted, setFormSubmission } = useFormResultController();

  const shownStorageId = customization?.shownStorageId
    ? `profitSalesEmailPopupShown@${customization?.shownStorageId}`
    : 'profitSalesEmailPopupShown';

  const startedTimeStorageId = customization?.shownStorageId
    ? `profitSalesEmailPopupStartedTime@${customization?.shownStorageId}`
    : 'profitSalesEmailPopupStartedTime';

  const timeLeftStorageId = customization?.shownStorageId
    ? `profitSalesEmailPopupTimeLeft@${customization?.shownStorageId}`
    : 'profitSalesEmailPopupTimeLeft';

  const shouldShowPopup = (): boolean => {
    const popupShown = getLocalStorage(shownStorageId);
    const popShownAsTime = parseInt(popupShown as string);

    if (isEmailSubscriptionSubmitted()) return false;

    if (isNaN(popShownAsTime)) return true;

    const timeDifferenceInHours = (new Date().getTime() - popShownAsTime) / 1000 / 60 / 60;

    return timeDifferenceInHours >= 72;
  };

  const handleStorageOnModalOpen = (): void => {
    setLocalStorage(shownStorageId, new Date().getTime().toString());
    deleteLocalStorage(startedTimeStorageId);
    deleteLocalStorage(timeLeftStorageId);
  };

  const handleMouseLeave = (event: MouseEvent) => {
    if (!shouldShowPopup()) return;

    if (
      event.clientY <= 0 ||
      event.clientX <= 0 ||
      event.clientX >= window.innerWidth ||
      event.clientY >= window.innerHeight
    ) {
      handleOpen();
      handleStorageOnModalOpen();
    }
  };

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined' || isPaused) return;

    document.addEventListener('mouseleave', handleMouseLeave);

    // Form ID for Email Capture Footer Subscriber
    const formId = '79e916cb-d4f8-4ea5-990c-ee51c8259e7b';
    createHubSpotForm({
      formContainer: '#hubspotEmailPopupForm',
      formId,
      onFormReadyCallback: () => {
        // Prefill form position field
        prefillField('hubspotEmailPopupForm', 'input[name="form_position"]', 'modal');
      },
      onFormSubmitCallback: () => {
        setBlockButton(true);
      },
      onFormSubmittedCallback: () => {
        setSuccessVisible(true);
        setFormSubmission();
      },
      errorCallback: () => {
        const failureTimeout = setTimeout(() => {
          setFailureVisible(true);
          clearTimeout(failureTimeout);
        }, 2000);
      },
    });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPaused]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!shouldShowPopup()) return;

    if (isPaused) {
      // Add 10s as compensation for the time the popup was paused
      pauseCompensationRef.current = 10000;
      return;
    }

    let timeLeft: string | number = getLocalStorage(timeLeftStorageId);
    let profitSalesEmailPopupStartedTime: string | number = getLocalStorage(startedTimeStorageId);
    const timeToShow = 1000 * 60;

    // First time setting up
    if (!timeLeft || !profitSalesEmailPopupStartedTime) {
      timeLeft = timeToShow;
      setLocalStorage(timeLeftStorageId, timeLeft.toString());

      profitSalesEmailPopupStartedTime = new Date().getTime();
      setLocalStorage(startedTimeStorageId, profitSalesEmailPopupStartedTime.toString());
    }

    // Includes compensation for the time the popup was paused
    const timeLeftForTimer = Math.max(parseInt(timeLeft as string), 0) + pauseCompensationRef.current;

    const timer = setTimeout(() => {
      // prevent showing popup if already shown by mouseleave
      if (!shouldShowPopup()) {
        return;
      }

      handleOpen();
      handleStorageOnModalOpen();
    }, timeLeftForTimer);

    return () => {
      // Prevent localStorage changes after render
      if (!shouldShowPopup()) {
        clearTimeout(timer);
        return;
      }

      const timeDifference = new Date().getTime() - parseInt(profitSalesEmailPopupStartedTime as string);

      setLocalStorage(timeLeftStorageId, (timeToShow - timeDifference).toString());
      clearTimeout(timer);
    };
  }, [isPopupVisible, isPaused]);

  const handleOpen = () => {
    setIsPopupVisible(true);
  };

  const handleClose = () => {
    setIsPopupVisible(false);
  };

  const title = customization?.title || 'Could this be THE DECISION that changes everything?';
  const description = customization?.description;

  return (
    <EmailPopupMainWrapper
      className={isPopupVisible ? 'isVisible' : null}
      id='email-popup-main-wrapper'
    >
      <EmailPopupMainBackgroundOverlay onClick={handleClose} />
      <EmailPopupMainContainer>
        <EmailPopupMainClose
          onClick={() => {
            setIsPopupVisible(false);
          }}
        >
          <CloseIcon rectColor={palette.greyText} />
        </EmailPopupMainClose>
        <EmailPopupMainImage>
          <EmailPopupMainBackgroundImage>
            <GatsbyImage
              image={peopleMagicProfitSalesBackground.gatsbyImageData}
              alt='Purpose now email book'
            />
          </EmailPopupMainBackgroundImage>
          <GatsbyImage
            image={peopleMagicProfitSales.gatsbyImageData}
            alt='Purpose now email book'
          />
        </EmailPopupMainImage>
        <EmailPopupMainLayout>
          <EmailPopupMainText className={successVisible || failureVisible ? 'hideMainText' : null}>
            <Heading
              text={title}
              weight='normal'
              standardSize='1.5'
              align='center'
              color={palette.greyText}
            />
            {description ? (
              <Paragraph
                weight='light'
                standardSize='1'
                align='center'
                color={palette.greyText}
              >
                {description}
              </Paragraph>
            ) : (
              <Paragraph
                weight='light'
                standardSize='1'
                align='center'
                color={palette.greyText}
              >
                Enter your email to get <b>9 SIMPLE STEPS to $1M</b> and a special bonus: a FREE copy of the
                best-selling book, <i>Purpose</i>
              </Paragraph>
            )}
            <EmailPopupMainForm>
              <EmailPopupHubspotForm id='hubspotEmailPopupForm' />
              <ButtonPrimary
                type='submit'
                text='Unlock People Magic Profit'
                disabled={blockButton ? true : false}
                colorScheme='Sunshine'
                onClick={submitHubSpotForm}
              />
              <Paragraph
                text={`By signing up you’re opting in to emails from us about the free masterclass, the podcast, and other helpful guides and goodies. \n \n You can unsubscribe at any time.`}
                size='0.625rem'
                align='center'
                weight='light'
                color={palette.greyText}
              />
            </EmailPopupMainForm>
          </EmailPopupMainText>

          <EmailPopupMainText className={successVisible ? 'isVisible' : 'hideMainText'}>
            <EmailPopupMainThankYou>
              <ThankYouWithLink />
            </EmailPopupMainThankYou>
          </EmailPopupMainText>

          <EmailPopupMainText className={failureVisible ? 'isVisible' : 'hideMainText'}>
            <EmailPopupMainThankYou>
              <Heading
                text='Something went wrong!'
                weight='normal'
                standardSize='2.125'
                color={palette.greyText}
                align='center'
              />
              <Paragraph
                text='Please, try again later.'
                weight='light'
                size='1rem'
                color={palette.greyText}
                align='center'
              />
              <ButtonPrimary
                type='button'
                text='Return to home'
                onClick={handleClose}
                colorScheme='Sunshine'
              />
            </EmailPopupMainThankYou>
          </EmailPopupMainText>
        </EmailPopupMainLayout>
      </EmailPopupMainContainer>
    </EmailPopupMainWrapper>
  );
};

export default ProfitSalesEmailPopupMain;
